import { WarningIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  Alert,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Checkbox,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep } from 'lodash';
import { DateTime as luxonDatetime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { Date as dateType, Util } from '@finley-technologies/finley-types';

import { FullDateRangePicker } from '../DateRangePicker';
import {
  DaysOfTheWeekSelectButtons,
  handleSelectDays,
} from './components/DayOfTheWeek';
import { ManualDatesModal } from './components/ManualDates';
import { ReviewDatesComponentFromConfigFormState } from './components/ReviewDates';
import {
  DayOfTheWeek,
  dayOfTheWeekToNumberMap,
  ConfigFormState,
  OFFSET_DAY_UNIT_OPTIONS,
  FREQUENCY,
  numberToDayOfTheWeekMap,
} from './types';
import {
  convertConfigFormStatesToRecurringDateConfigs,
  makeAdverb,
  ordinalSuffix,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Counter } from 'packages/common-frontend/components/Counter';
import { IconButton } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  getExcludedDateConfigs,
  getManualAddDates,
  getManualExcludeDates,
  getRecurringDateConfigs,
  RecurringDateFormProps,
} from 'packages/common-frontend/components/RecurringDateForm/RecurringDateForm';
import { Select } from 'packages/common-frontend/components/Select';
import { Typography } from 'packages/common-frontend/components/Typography';

const PERIOD_OPTIONS_V2 = ({
  startDate,
  frequency,
}: {
  startDate: string;
  frequency: dateType.RecurringDate.ReccuringFrequencyPeriod;
}) => [
  {
    label: `${makeAdverb(frequency)} from ${startDate}`,
    value: 'calendarDate',
  },
  {
    label: `Start of ${frequency}`,
    value: 'startOf',
  },
  {
    label: `End of ${frequency}`,
    value: 'endOf',
  },
  {
    label: 'Custom',
    value: 'weekdayByOccurrence',
  },
];

type RecurringDatesOptionsGridProps = {
  titleNode: React.ReactNode;
  config: ConfigFormState;
  setConfig: React.Dispatch<React.SetStateAction<ConfigFormState[]>>;
  indexOfConfig: number;
};
const RecurringDatesV2Grid = ({
  titleNode,
  config,
  setConfig,
  indexOfConfig,
}: RecurringDatesOptionsGridProps) => {
  // --------------------- REACT HOOKS ---------------------
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: config.startDate || new Date(),
    endDate: config.endDate || new Date(),
  });

  const [frequency, setFrequency] = useState<
    | dateType.RecurringDate.FrequencyPeriod
    | dateType.RecurringDate.ReccuringFrequencyPeriod
    | undefined
  >(config.frequency);

  const [cadence, setCadence] = useState(config.cadence || 1);
  const [occurrence, setOccurrence] = useState(config.occurrence || 1);

  const [
    period,
    setPeriod,
  ] = useState<dateType.RecurringDate.BaseDateConfigType>(
    config.period || 'calendarDate',
  );

  const [offsetDays, setOffsetDays] = useState<number>(config.offsetDays || 0);
  const [offsetDayUnit, setoffsetDayUnit] = useState<dateType.DayUnit>(
    config.offsetDayUnit || 'calendarDay',
  );
  const [offsetSign, setOffsetSign] = useState<'before' | 'after'>(
    // eslint-disable-next-line no-nested-ternary
    config.offsetDays ? (config.offsetDays >= 0 ? 'after' : 'before') : 'after',
  );
  const [
    offsetNonBusinessDayAdjustment,
    setOffsetNonBusinessDayAdjustment,
  ] = useState<dateType.NonBusinessDayAdjustment | undefined>(
    config.offsetNonBusinessDayAdjustment,
  );

  const [selectedDays, setSelectedDays] = useState<DayOfTheWeek[]>(
    config.selectedDays || [],
  );

  const [touched, setTouched] = useState(config.touched);

  // --------------------- ERRORS FROM VALIDATING INPUTS ---------------------
  const [errorDateRange, setErrorDateRange] = useState<string>('');
  const [errorFrequency, setErrorFrequency] = useState<string>('');
  const [errorSelectedDay, setErrorSelectedDays] = useState<string>('');

  useEffect(() => {
    if (frequency) {
      setErrorFrequency(
        period !== 'weekday' && frequency === 'week'
          ? 'Frequency cannot be `week`'
          : '',
      );
    } else {
      setErrorFrequency('Frequency cannot be empty');
    }
    setErrorSelectedDays(
      (period === 'weekday' || period === 'weekdayByOccurrence') &&
        (!selectedDays || selectedDays.length < 1 || selectedDays.length > 7)
        ? 'Selected days of the week error'
        : '',
    );
    setErrorDateRange(
      !dateRange.startDate || !dateRange.endDate
        ? 'Missing start and/or end date'
        : '',
    );
  }, [
    dateRange.endDate,
    dateRange.startDate,
    frequency,
    occurrence,
    period,
    selectedDays,
  ]);

  const sumOfInputErrors = useMemo(() => {
    const err = [errorDateRange, errorFrequency, errorSelectedDay]
      .filter((e) => e.length > 0)
      .join(', ');
    return err;
  }, [errorDateRange, errorFrequency, errorSelectedDay]);

  // --------------------- VALIDATE & CREATE CONFIG ---------------------
  useEffect(() => {
    const result = recurringDate.validateAndCreateConfig({
      cadence,
      offsetDays,
      offsetDayUnit,
      offsetNonBusinessDayAdjustment,
      period,
      weekdays: selectedDays.map((day) => dayOfTheWeekToNumberMap[day]),
      occurrence,
      startDate: dateRange.startDate
        ? commonDate.dateToFullDateString(dateRange.startDate)
        : undefined,
      endDate: dateRange.endDate
        ? commonDate.dateToFullDateString(dateRange.endDate)
        : undefined,
      frequency,
    });
    setConfig((current) => {
      const newArr = [...current];
      newArr[indexOfConfig] = {
        isValid: !!result.config,
        isRelative: false,
        touched,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        frequency,
        cadence,
        occurrence,
        period,
        offsetDays,
        offsetDayUnit,
        offsetNonBusinessDayAdjustment,
        selectedDays,
      };
      return newArr;
    });
  }, [
    cadence,
    touched,
    dateRange.endDate,
    dateRange.startDate,
    frequency,
    indexOfConfig,
    occurrence,
    offsetDayUnit,
    offsetDays,
    offsetNonBusinessDayAdjustment,
    period,
    selectedDays,
    setConfig,
  ]);

  return (
    <Stack
      spacing="20px"
      borderRadius="8px"
      borderWidth="1px"
      padding="24px"
      width="full"
    >
      <HStack>
        {titleNode}
        <Spacer />
        <IconButton
          ariaLabel="Delete config"
          label=""
          icon={<Icon icon="trash-can" />}
          size="sm"
          variant="v2-ghost"
          onClick={() => {
            setConfig((current) => {
              const newArr = [...current];
              newArr.splice(indexOfConfig, 1);
              return newArr;
            });
          }}
        />
      </HStack>

      <SimpleGrid
        columns={2}
        spacingX="40px"
        spacingY="20px"
        templateColumns="1fr 4fr"
      >
        <Text fontSize="md" mt={2}>
          Select Dates
        </Text>
        <FullDateRangePicker
          onSelectDateRange={(newDateRange) => {
            const [newStartDate, newEndDate] = newDateRange;
            if (newStartDate && newEndDate) {
              setDateRange({
                startDate: newStartDate,
                endDate: newEndDate,
              });
            }
          }}
          currentDateRange={{
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }}
          width="100%"
          showPreSelectedOptions={false}
        />

        <Text fontSize="md" mt={2}>
          Repeat every
        </Text>
        <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
          <NumberInput
            value={cadence}
            min={1}
            onChange={(newValue) => {
              setCadence(Number(newValue));
            }}
            width="105px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>

          <Select
            placeholderLabel="Select frequency"
            value={frequency}
            isInvalid={errorFrequency.length > 0}
            options={FREQUENCY.map((option) => ({
              label: `${option}s`,
              value: option,
            }))}
            onChange={(value) => {
              setFrequency(
                value as dateType.RecurringDate.FrequencyPeriod | undefined,
              );

              if (value === 'week') {
                setPeriod('weekday');
                const weekdayNumber = luxonDatetime.fromJSDate(
                  dateRange.startDate,
                ).weekday;

                const weekday =
                  numberToDayOfTheWeekMap[weekdayNumber as dateType.Weekday];

                setSelectedDays((curr) =>
                  curr.length === 0 ? [weekday] : curr,
                );
              } else if (period === 'weekdayByOccurrence') {
                const weekdayNumber = luxonDatetime.fromJSDate(
                  dateRange.startDate,
                ).weekday;

                const weekday =
                  numberToDayOfTheWeekMap[weekdayNumber as dateType.Weekday];

                setSelectedDays((curr) =>
                  curr.length === 0 ? [weekday] : curr,
                );
              } else {
                setSelectedDays([]);
              }

              if (value !== 'week' && period === 'weekday') {
                setPeriod('calendarDate');
              }

              setTouched(true);
            }}
          />
        </HStack>

        {frequency !== undefined && (
          <Text fontSize="md" mt={2}>
            Repeats On
          </Text>
        )}
        {frequency === 'week' ? (
          <DaysOfTheWeekSelectButtons
            onSelectDay={(day) => handleSelectDays(day, setSelectedDays)}
            selectedDays={selectedDays}
            error={errorSelectedDay}
          />
        ) : (
          <Select
            value={period}
            isDisabled={frequency === undefined}
            options={
              frequency === undefined
                ? []
                : PERIOD_OPTIONS_V2({
                    startDate: commonDate.dateToHumanDateString(
                      dateRange.startDate,
                    ),
                    frequency,
                  })
            }
            onChange={(value) => {
              setPeriod(value as dateType.RecurringDate.BaseDateConfigType);

              if (value === 'weekdayByOccurrence') {
                const weekdayNumber = luxonDatetime.fromJSDate(
                  dateRange.startDate,
                ).weekday;

                const weekday =
                  numberToDayOfTheWeekMap[weekdayNumber as dateType.Weekday];

                setSelectedDays((curr) =>
                  curr.length === 0 ? [weekday] : curr,
                );
              }
            }}
          />
        )}

        {period === 'weekdayByOccurrence' && <Text fontSize="md" mt={2} />}
        {period === 'weekdayByOccurrence' && (
          <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
            <NumberInput
              value={ordinalSuffix(occurrence)}
              min={1}
              onChange={(newValue) => setOccurrence(Number(newValue))}
              width="105px"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>

            <DaysOfTheWeekSelectButtons
              onSelectDay={(day) => handleSelectDays(day, setSelectedDays)}
              selectedDays={selectedDays}
              error={errorSelectedDay}
            />

            <Text fontSize="md">{`of each ${frequency}`}</Text>
          </HStack>
        )}

        {frequency !== undefined && (
          <Text fontSize="md" mt={2}>
            Offset
          </Text>
        )}
        {frequency !== undefined && (
          <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
            <NumberInput
              value={Math.abs(offsetDays)}
              onChange={(newValue) => {
                if (offsetSign === 'before') {
                  setOffsetDays(Number(newValue) * -1);
                }
                if (offsetSign === 'after') {
                  setOffsetDays(Number(newValue));
                }
              }}
              width="105px"
              min={0}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Select
              value={offsetDayUnit}
              options={OFFSET_DAY_UNIT_OPTIONS}
              onChange={(value) => {
                if (value === 'businessDay') {
                  setOffsetNonBusinessDayAdjustment(undefined);
                }
                setoffsetDayUnit(value as dateType.DayUnit);
              }}
            />
            <Select
              value={offsetSign}
              options={['before', 'after'].map((option) => ({
                label: option,
                value: option,
              }))}
              onChange={(value) => {
                if (value === 'before') {
                  setOffsetSign('before');
                  setOffsetDays((curr) => Math.abs(curr) * -1);
                }

                if (value === 'after') {
                  setOffsetSign('after');
                  setOffsetDays((curr) => Math.abs(curr));
                }
              }}
            />
          </HStack>
        )}

        {/* Non business day adjustment checkbox */}
        {frequency !== undefined && offsetDayUnit === 'calendarDay' && (
          <Text fontSize="md" mt={2} />
        )}
        {frequency !== undefined && offsetDayUnit === 'calendarDay' && (
          <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
            <Checkbox
              isChecked={!!offsetNonBusinessDayAdjustment}
              onChange={(e) =>
                setOffsetNonBusinessDayAdjustment(
                  e.target.checked ? 'after' : undefined,
                )
              }
              fontSize="x-small"
              color={colors.gray[50]}
            >
              Business days only
            </Checkbox>

            {offsetNonBusinessDayAdjustment !== undefined && (
              <>
                <Text>-</Text>

                <Text>Find</Text>
                <Select
                  value={offsetNonBusinessDayAdjustment}
                  options={[
                    {
                      label: 'Prior',
                      value: 'before' as dateType.NonBusinessDayAdjustment,
                    },
                    {
                      label: 'Next',
                      value: 'after' as dateType.NonBusinessDayAdjustment,
                    },
                  ]}
                  onChange={(value) => {
                    setOffsetNonBusinessDayAdjustment(value);
                  }}
                  buttonProps={{ fontSize: 'small' }}
                />

                <Text>Business Day</Text>
              </>
            )}
          </HStack>
        )}
      </SimpleGrid>

      {config.touched && (!config.isValid || sumOfInputErrors.length > 0) && (
        <HStack
          spacing="8px"
          bg="red.100"
          borderRadius="8px"
          padding="15px"
          fontSize="sm"
        >
          <WarningIcon color="red.500" />
          <Text color="red.500">{sumOfInputErrors}</Text>
        </HStack>
      )}
    </Stack>
  );
};

export const RecurringDateV2Body = ({
  savedConfig,
  setSavedConfig,
  mode,
}: RecurringDateFormProps) => {
  if (mode === 'global') {
    finleyAssert(
      savedConfig.setup.type === 'static',
      'a Static recurring date setup type is required to use the Static Recurring Date Form',
    );
  }
  const getInitialRecurringDateConfigs = useCallback(() => {
    const shell = { isValid: false, isRelative: false, touched: false };

    if (mode === 'setup') {
      const configs = getRecurringDateConfigs(savedConfig);
      return configs.length > 0 ? configs : [shell];
    }
    if (mode === 'global' && savedConfig.setup.type === 'static') {
      const configs = getRecurringDateConfigs(savedConfig.setup.config);
      return configs.length > 0 ? configs : [shell];
    }

    return [shell];
  }, [mode, savedConfig]);

  const getInitialExcludedDateConfigs = useCallback(() => {
    if (mode === 'setup') {
      return getExcludedDateConfigs(savedConfig);
    }
    if (mode === 'global' && savedConfig.setup.type === 'static') {
      return getExcludedDateConfigs(savedConfig.setup.config);
    }

    return [{ isValid: false, isRelative: false, touched: false }];
  }, [mode, savedConfig]);

  const getInitialManualAddDates = useCallback(() => {
    if (mode === 'setup') {
      return getManualAddDates(savedConfig);
    }
    if (mode === 'global' && savedConfig.setup.type === 'static') {
      return getManualAddDates(savedConfig.setup.config);
    }

    return [];
  }, [mode, savedConfig]);

  const getInitialManualExcludeDates = useCallback(() => {
    if (mode === 'setup') {
      return getManualExcludeDates(savedConfig);
    }
    if (mode === 'global' && savedConfig.setup.type === 'static') {
      return getManualExcludeDates(savedConfig.setup.config);
    }

    return [];
  }, [mode, savedConfig]);

  // configs
  const [recurringDateConfigs, setRecurringDateConfigs] = useState<
    ConfigFormState[]
  >(getInitialRecurringDateConfigs());

  const [excludedDateConfigs, setExcludedDateConfigs] = useState<
    ConfigFormState[]
  >(getInitialExcludedDateConfigs());
  // datelists
  const [manualAddDates, setManualAddDates] = useState<Util.FullDateStr[]>(
    getInitialManualAddDates(),
  );
  const [manualExcludeDates, setManualExcludeDates] = useState<
    Util.FullDateStr[]
  >(getInitialManualExcludeDates());

  // number of valid dates in review date accordion
  const [reviewDatesCount, setReviewDatesCount] = useState<number>(0);

  const [cannotSaveConfig, setCannotSaveConfig] = useState<boolean>(
    !recurringDateConfigs.every((c) => c.isValid),
  );

  // update state that is passed in
  useEffect(() => {
    const allRecurring = convertConfigFormStatesToRecurringDateConfigs(
      recurringDateConfigs.filter((c) => c.isValid && !c.isRelative),
    ).concat(manualAddDates.length > 0 ? [manualAddDates] : []);

    const allExcluded = convertConfigFormStatesToRecurringDateConfigs(
      excludedDateConfigs.filter((c) => c.isValid && !c.isRelative),
    ).concat(manualExcludeDates.length > 0 ? [manualExcludeDates] : []);

    const isDisabled = !recurringDateConfigs.every((c) => c.isValid);
    setCannotSaveConfig(isDisabled);

    if (!isDisabled) {
      if (mode === 'setup') {
        setSavedConfig({
          recurringDateConfigs: allRecurring,
          exclusions: allExcluded,
        });
      } else if (mode === 'global' && savedConfig.setup.type === 'static') {
        setSavedConfig({
          ...savedConfig,
          setup: {
            ...savedConfig.setup,
            config: {
              recurringDateConfigs: allRecurring,
              exclusions: allExcluded,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    excludedDateConfigs,
    manualAddDates,
    manualExcludeDates,
    recurringDateConfigs,
    setSavedConfig,
    mode,
  ]);

  const [isAddManualDatesOpen, setIsAddManualDatesOpen] = useState(false);

  const [touched, setTouched] = useState(false);

  useEffect(() => {
    function onTouch() {
      setTouched(
        recurringDateConfigs.some((c) => c.touched) ||
          excludedDateConfigs.some((c) => c.touched),
      );
    }
    const interval = setInterval(onTouch, 1000);

    return () => clearInterval(interval);
  }, [excludedDateConfigs, recurringDateConfigs]);

  return (
    <Box width="full">
      {touched && cannotSaveConfig && (
        <Alert status="error" mb={4}>
          Cannot save this config, remediate errors first.
        </Alert>
      )}

      <Stack spacing="25px">
        {recurringDateConfigs.map((config, i) => {
          return (
            <RecurringDatesV2Grid
              key={JSON.stringify(config)}
              config={config}
              setConfig={setRecurringDateConfigs}
              indexOfConfig={i}
              titleNode={
                <Text fontSize="20px" fontWeight="semibold">
                  Set Recurring
                </Text>
              }
            />
          );
        })}

        {excludedDateConfigs.map((config, i) => {
          return (
            <RecurringDatesV2Grid
              key={JSON.stringify(config)}
              config={config}
              setConfig={setExcludedDateConfigs}
              indexOfConfig={i}
              titleNode={
                <HStack spacing={4}>
                  <Icon icon="circle-minus" />
                  <Text fontSize="20px" fontWeight="semibold">
                    Set Exclusions
                  </Text>
                </HStack>
              }
            />
          );
        })}
      </Stack>

      <Stack
        my={4}
        spacing="20px"
        borderRadius="8px"
        borderWidth="1px"
        padding="24px"
      >
        <ReviewDatesComponentFromConfigFormState
          recurringConfigs={recurringDateConfigs}
          excludedConfigs={excludedDateConfigs}
          manualAddDates={manualAddDates}
          manualExcludeDates={manualExcludeDates}
          setReviewDatesCount={(num: number) => setReviewDatesCount(num)}
          onAddToExclusion={(date) => {
            setManualExcludeDates((curr) => [...curr, date]);
          }}
          onRemoveFromExclusion={(date) => {
            // if in manual exclude dates, then remove
            setManualExcludeDates((curr) => {
              if (curr.includes(date)) {
                return curr.filter((d) => d !== date);
              }

              return curr;
            });

            // otherwise, sarah todo: figure out how to find which exclusion config is creating this
          }}
          titleNode={
            <HStack width="full">
              <HStack gap="1rem">
                <Typography fontWeight="semibold" trim>
                  Review Dates
                </Typography>
                <Counter count={reviewDatesCount} />
              </HStack>

              <Spacer />

              <Menu>
                <MenuButton
                  as={Button}
                  variant="v2-ghost"
                  fontSize="sm"
                  fontWeight="medium"
                  leftIcon={<Icon icon="plus" />}
                  isDisabled={cannotSaveConfig}
                >
                  Add
                </MenuButton>

                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setIsAddManualDatesOpen(true);
                    }}
                  >
                    Add Manual Date
                  </MenuItem>

                  <MenuItem
                    onClick={() =>
                      setRecurringDateConfigs((current) => [
                        ...current,
                        { isValid: false, isRelative: false, touched: false },
                      ])
                    }
                  >
                    Add Recurring Date
                  </MenuItem>

                  <MenuItem
                    onClick={() =>
                      setExcludedDateConfigs((current) => [
                        ...current,
                        { isValid: false, isRelative: false, touched: false },
                      ])
                    }
                  >
                    Add Recurring Exclusion Date
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          }
        />
      </Stack>

      <ManualDatesModal
        isOpen={isAddManualDatesOpen}
        onClose={() => {
          setIsAddManualDatesOpen(false);
        }}
        title="Add"
        dateList={manualAddDates}
        setManualDates={setManualAddDates}
      />
    </Box>
  );
};

// NOTE: this has mode "setup", if you want to use "global" please use `RecurringDateV2Body` directly in your form
export const RecurringDateV2 = ({
  onCancel,
  onSave,
  recurringDateConfig,
}: {
  onCancel: Function;
  onSave: (config: dateType.RecurringDate.Config) => void;
  recurringDateConfig: dateType.RecurringDate.Config;
}) => {
  return (
    <Box width="full">
      <Formik
        initialValues={cloneDeep(recurringDateConfig)}
        onSubmit={(props) => {
          onSave(props);
        }}
      >
        {(formProps: FormikProps<dateType.RecurringDate.Config>) => {
          return (
            <Form onSubmit={formProps.handleSubmit}>
              <RecurringDateV2Body
                savedConfig={formProps.values}
                setSavedConfig={formProps.setValues}
                mode="setup"
              />
              <HStack width="full" mt={4}>
                <Spacer />
                <Button
                  variant="v2-ghost"
                  mr="5px"
                  onClick={() => {
                    onCancel();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    formProps.submitForm();
                  }}
                >
                  Save
                </Button>
              </HStack>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};
